import { Children, Fragment, isValidElement, useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type KeyboardEvent, type ReactNode } from "react";
import { createPortal } from "react-dom";

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps {
  id?: string;
  'aria-label'?: string;
  size?: 'default' | 'compact';
  name?: string;
  value: string;
  options?: readonly SelectOption[];
  children?: ReactNode;
  onValueChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  invalid?: boolean;
  describedBy?: string;
}

function optionText(children: ReactNode): string {
  return Children.toArray(children).map(child => typeof child === 'string' || typeof child === 'number' ? String(child) : isValidElement<{ children?: ReactNode }>(child) ? optionText(child.props.children) : '').join('');
}

function childOptions(children: ReactNode): SelectOption[] {
  const result: SelectOption[] = [];
  Children.forEach(children, child => {
    if (!isValidElement<{ value?: string; disabled?: boolean; children?: ReactNode }>(child)) return;
    if (child.type === Fragment) result.push(...childOptions(child.props.children));
    else if (child.type === 'option') {
      const label = optionText(child.props.children);
      result.push({ value: child.props.value ?? label, label, disabled: child.props.disabled });
    }
  });
  return result;
}

function enabledOptionIndex(
  options: readonly SelectOption[],
  startIndex: number,
  direction: 1 | -1
): number {
  if (options.length === 0) return -1;

  for (let offset = 1; offset <= options.length; offset += 1) {
    const candidate = (startIndex + direction * offset + options.length) % options.length;
    if (!options[candidate]?.disabled) return candidate;
  }

  return -1;
}

function boundaryOptionIndex(options: readonly SelectOption[], fromEnd = false): number {
  const indexes = options.map((_, index) => index);
  if (fromEnd) indexes.reverse();
  return indexes.find((index) => !options[index]?.disabled) ?? -1;
}

export function Select({
  id: providedId,
  'aria-label': ariaLabel,
  size = 'default',
  name,
  value,
  options: providedOptions,
  children,
  onValueChange,
  placeholder = "Select an option",
  disabled = false,
  required = false,
  invalid = false,
  describedBy
}: SelectProps) {
  const generatedId = useId();
  const id = providedId ?? `select-${generatedId}`;
  const options = useMemo(() => providedOptions ?? childOptions(children), [providedOptions, children]);
  const [open, setOpen] = useState(false);
  const [validationFailed, setValidationFailed] = useState(false);
  const [menuStyle, setMenuStyle] = useState<CSSProperties>({ position: 'fixed' });
  const [activeIndex, setActiveIndex] = useState(-1);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listboxRef = useRef<HTMLDivElement>(null);
  const listboxId = `${id}-listbox`;
  const selectedIndex = options.findIndex((option) => option.value === value);
  const selectedOption = selectedIndex >= 0 ? options[selectedIndex] : undefined;

  function optionId(index: number) {
    return `${id}-option-${index}`;
  }

  function openMenu(preferred: "selected" | "first" | "last" = "selected") {
    if (disabled) return;
    const nextIndex = preferred === "last"
      ? boundaryOptionIndex(options, true)
      : preferred === "first"
        ? boundaryOptionIndex(options)
        : selectedIndex >= 0 && !options[selectedIndex]?.disabled
          ? selectedIndex
          : boundaryOptionIndex(options);
    setActiveIndex(nextIndex);
    setOpen(true);
  }

  function closeMenu(returnFocus = false) {
    setOpen(false);
    if (returnFocus) triggerRef.current?.focus();
  }

  function selectOption(index: number) {
    const option = options[index];
    if (!option || option.disabled) return;
    onValueChange(option.value);
    setValidationFailed(false);
    closeMenu(true);
  }

  function handleKeyDown(event: KeyboardEvent<HTMLButtonElement>) {
    if (disabled) return;

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        if (!open) openMenu("selected");
        else setActiveIndex((current) => enabledOptionIndex(options, current, 1));
        break;
      case "ArrowUp":
        event.preventDefault();
        if (!open) openMenu("last");
        else setActiveIndex((current) => enabledOptionIndex(options, current, -1));
        break;
      case "Home":
        if (open) {
          event.preventDefault();
          setActiveIndex(boundaryOptionIndex(options));
        }
        break;
      case "End":
        if (open) {
          event.preventDefault();
          setActiveIndex(boundaryOptionIndex(options, true));
        }
        break;
      case "Enter":
      case " ":
        event.preventDefault();
        if (open) selectOption(activeIndex);
        else openMenu("selected");
        break;
      case "Escape":
        if (open) {
          event.preventDefault();
          closeMenu(true);
        }
        break;
      case "Tab":
        if (open) closeMenu();
        break;
    }
  }

  useEffect(() => {
    if (!open) return;

    function handlePointerDown(event: PointerEvent) {
      if (!rootRef.current?.contains(event.target as Node) && !listboxRef.current?.contains(event.target as Node)) closeMenu();
    }

    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [open]);

  useEffect(() => {
    if (disabled && open) setOpen(false);
  }, [disabled, open]);

  useLayoutEffect(() => {
    if (!open) return;
    const position = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;
      const gap = 6;
      const below = window.innerHeight - rect.bottom - gap - 8;
      const above = rect.top - gap - 8;
      const upward = below < 160 && above > below;
      const maxHeight = Math.max(48, Math.min(280, window.innerHeight * .45, upward ? above : below));
      const height = Math.min(maxHeight, listboxRef.current?.scrollHeight || maxHeight);
      const width = Math.min(rect.width, window.innerWidth - 16);
      setMenuStyle({ position: 'fixed', width, maxHeight, left: Math.max(8, Math.min(rect.left, window.innerWidth - width - 8)), top: upward ? rect.top - gap - height : rect.bottom + gap });
    };
    const scroll = (event: Event) => { if (!(event.target instanceof Node) || !listboxRef.current?.contains(event.target)) position(); };
    position();
    window.addEventListener('resize', position);
    window.addEventListener('scroll', scroll, true);
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(position);
    if (triggerRef.current) observer?.observe(triggerRef.current);
    return () => { window.removeEventListener('resize', position); window.removeEventListener('scroll', scroll, true); observer?.disconnect(); };
  }, [open, options]);

  useEffect(() => {
    if (!open || activeIndex < 0) return;
    document.getElementById(optionId(activeIndex))?.scrollIntoView?.({ block: "nearest" });
  }, [activeIndex, open]);

  return (
    <div className={`ui-select${size === 'compact' ? ' ui-select-compact' : ''}`} ref={rootRef}>
      <button
        aria-label={ariaLabel}
        aria-activedescendant={open && activeIndex >= 0 ? optionId(activeIndex) : undefined}
        aria-controls={listboxId}
        aria-describedby={describedBy}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-invalid={invalid || validationFailed || undefined}
        aria-required={required || undefined}
        className="ui-select-trigger"
        disabled={disabled}
        data-value={value}
        id={id}
        onClick={() => open ? closeMenu() : openMenu("selected")}
        onKeyDown={handleKeyDown}
        ref={triggerRef}
        role="combobox"
        type="button"
      >
        <span className={selectedOption ? "ui-select-value" : "ui-select-placeholder"}>
          {selectedOption?.label ?? placeholder}
        </span>
        <svg
          aria-hidden="true"
          className="ui-select-chevron"
          viewBox="0 0 20 20"
        >
          <path d="m5 7.5 5 5 5-5" />
        </svg>
      </button>
      {required ? <input className="ui-select-validation" aria-hidden="true" tabIndex={-1} name={name} value={value} required disabled={disabled} onChange={() => {}} onInvalid={event => { event.preventDefault(); setValidationFailed(true); triggerRef.current?.focus(); }} /> : name ? <input type="hidden" name={name} value={value} disabled={disabled} /> : null}

      {open ? createPortal(
        <div className="ui-select-listbox" style={menuStyle} ref={listboxRef} id={listboxId} role="listbox" aria-label={ariaLabel} aria-labelledby={ariaLabel ? undefined : id}>
          {options.map((option, index) => {
            const selected = option.value === value;
            const active = index === activeIndex;
            return (
              <div
                aria-disabled={option.disabled || undefined}
                aria-selected={selected}
                className={[
                  "ui-select-option",
                  active ? "is-active" : "",
                  selected ? "is-selected" : "",
                  option.disabled ? "is-disabled" : ""
                ].filter(Boolean).join(" ")}
                id={optionId(index)}
                data-value={option.value}
                key={option.value}
                onClick={() => selectOption(index)}
                onPointerMove={() => {
                  if (!option.disabled) setActiveIndex(index);
                }}
                role="option"
              >
                <span>{option.label}</span>
                {selected ? <span aria-hidden="true" className="ui-select-check">✓</span> : null}
              </div>
            );
          })}
        </div>, triggerRef.current?.closest('dialog[open]') ?? document.body
      ) : null}
    </div>
  );
}
